import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useRouter } from 'vue-router'
import {
  useAlertStore,
  useCardStore,
  useRouteStore,
  useReportStore,
  useDealerStore,
  useI18nStore,
} from '@store'
import apirequest from '@utils/api-service.utils'
import {
  getItemLocalStorage,
  setToLocalStorage,
  getUserTimezone,
  getTimeStamp,
} from '@utils/helper.utils'
import { useI18n } from 'vue-i18n' // Import Vue i18n
import dayjs from 'dayjs'
import { encryptData } from '@/utils/crypto'
import endpoints from '@constants/endpoints.json'
import { i18n } from '@/main' // Import Vue i18n
import { generateBasicToken } from '@/utils/auth-token.utils'

const resetAllStores = () => {
  const cardStore = useCardStore()
  const routeStore = useRouteStore()
  const reportStore = useReportStore()
  const dealerStore = useDealerStore()
  cardStore.$reset()
  routeStore.$reset()
  reportStore.$reset()
  dealerStore.$reset()
}

const useAuthStore = defineStore('auth', () => {
  const { t } = i18n.global
  const router = useRouter()
  const alertStore = useAlertStore()
  const { pushAlert } = alertStore
  const isAuthenticated = ref(getItemLocalStorage('hasAuthenticated') || false)
  const accountData = ref(getItemLocalStorage('accountData'))
  const accountTypes = ref([])
  const section = ref('login')
  const questions = ref([])
  const loading = ref(false)
  const error = ref(false)
  const errorMessage = ref('')
  const accountInfo = ref(null)
  const lastUserActivityDate = ref(
    getItemLocalStorage('lastUserActivityDate') || null,
  )

  const updateSection = (value) => {
    section.value = value
  }

  const hasResource = (id) => {
    return !!accountData.value?.MENULIST?.find((menu) => menu.MENUID === id)
  }

  const fetchAccountTypes = async () => {
    try {
      // await getToken('LOGITECH', '11111')

      const res = await apirequest('getProductTypes', 'get')

      if (res.respcode === 0) {
        accountTypes.value = res.respbody.PRODLIST
      }
      return res
    } catch (e) {
      //
      pushAlert({
        title: t('alerts.errorTitle'),
        message: e.respmsg || t('alerts.error_fetching_account_types'),
        type: 'basic',
        notification_class: 'error',
        timeout: 5,
      })
      throw e
    }
  }

  const initResetPassword = ({ userName }) => {
    return new Promise(async (resolve, reject) => {
      window.loading.value = true
      try {
        const { respcode, respmsg, respbody } = await apirequest(
          'initResetPassword',
          'POST',
          {
            USERNAME: userName,
            USERTYPE: accountData.value?.USERTYPE || '',
          },
        )
        if (respcode === 0) {
          const { QNLIST } = respbody
          // pushAlert({
          //   title: t('alerts.successTitle'),
          //   message: t('alerts.passwordRequestSent'),
          //   type: 'basic',
          //   notification_class: 'success',
          //   timeout: 5,
          // })
          resolve({ QNLIST })
        } else {
          reject(respmsg)
        }
      } catch (e) {
        reject(e)
      } finally {
        window.loading.value = false
      }
    })
  }

  const updatePassword = (payload) => {
    return new Promise(async (resolve, reject) => {
      window.loading.value = true
      try {
        const { respcode, respmsg } = await apirequest(
          'updatePassword',
          'POST',
          { ...payload },
        )
        if (respcode === 0) {
          pushAlert({
            title: t('alerts.successTitle'),
            message: t('alerts.passwordChanged'),
            type: 'basic',
            notification_class: 'success',
            timeout: 5,
          })
          router.push({ name: 'login' })
          resolve(null)
        } else {
          reject(respmsg)
        }
      } catch (e) {
        reject(e)
      } finally {
        window.loading.value = false
      }
    })
  }

  const validateSecurityAns = ({ USERNAME, QNLIST }) => {
    return new Promise(async (resolve, reject) => {
      window.loading.value = true
      try {
        const { respcode, respmsg } = await apirequest(
          'validateSecurityAns',
          'POST',
          {
            QNLIST: QNLIST,
            USERNAME: USERNAME,
            USERTYPE: '',
          },
        )
        if (respcode === 0) {
          resolve({ respmsg })
        } else {
          reject(respmsg)
        }
      } catch (e) {
        reject(e)
      } finally {
        window.loading.value = false
      }
    })
  }

  const setPassword = (payload) => {
    return new Promise(async (resolve, reject) => {
      window.loading.value = true
      try {
        const { respcode, respmsg } = await apirequest('setPassword', 'POST', {
          ...payload,
        })
        if (respcode === 0) {
          pushAlert({
            title: t('alerts.successTitle'),
            message: respmsg || t('alerts.passwordChanged'),
            type: 'basic',
            notification_class: 'success',
            timeout: 5,
          })
          router.push({ name: 'login' })
          resolve()
        } else {
          throw { respmsg }
        }
      } catch ({ respmsg }) {
        pushAlert({
          title: t('alerts.errorTitle'),
          message: respmsg,
          type: 'basic',
          notification_class: 'error',
          timeout: 5,
        })
        reject(null)
      } finally {
        window.loading.value = false
      }
    })
  }

  const changePassword = ({
    currentPassword,
    newPassword,
    newPasswordConfirm,
  }) => {
    return new Promise(async (resolve, reject) => {
      window.loading.value = true
      try {
        const { respcode, respmsg } = await apirequest(
          'resetPassword',
          'POST',
          {
            NEWPWD: newPassword,
            OLDPWD: currentPassword,
            USERNAME: accountData.value?.USERNAME,
            USERTYPE: accountData.value?.USERTYPE,
          },
        )
        if (respcode === 0) {
          pushAlert({
            title: t('alerts.successTitle'),
            message: t('alerts.passwordChanged'),
            type: 'basic',
            notification_class: 'success',
            timeout: 5,
          })
        } else {
          throw { respmsg }
        }
        resolve({ respcode })
      } catch ({ respmsg }) {
        reject(respmsg)
      } finally {
        window.loading.value = false
      }
    })
  }

  const getToken = async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const timeStamp = getTimeStamp()
        let timezone = getUserTimezone()
        // const username = import.meta.env.VITE_AUTH_USERNAME
        // const password = import.meta.env.VITE_AUTH_PASSWORD
        // const combined = `${username}:${password}:${timeStamp}`
        //       const combined =
        // `${import.meta.env.VITE_AUTH_USERNAME}:` +
        // `${import.meta.env.VITE_AUTH_PASSWORD}:` +
        // `${timeStamp}`

        //       const basicToken = encryptData(
        //         combined,
        //         '3a956599c7edd8f5526bc681fa0616aa',
        //       )
        const basicToken = generateBasicToken(timeStamp)

        const response = await apirequest(
          'authenticate',
          'GET',
          null,
          {
            timezone: getUserTimezone(),
            timestamp: getTimeStamp(),
          },
          {
            Authorization: `Basic ${basicToken}`,
          },
        )

        if (response.token) {
          setToLocalStorage('token', response.token)
          resolve(response.token)
        } else {
          throw response
        }
      } catch (e) {
        pushAlert({
          title: t('alerts.errorTitle'),
          message: e.respmsg || t('alerts.error_generating_token'),
          type: 'basic',
          notification_class: 'error',
          timeout: 5,
        })

        reject(e)
      }
    })
  }

  const login = async (payload) => {
    try {
      loading.value = true

      setToLocalStorage('accountData', {
        USERTYPE: payload.accountType,
        USERNAME: payload.username,
      })
      const organization = JSON.parse(
        JSON.stringify(endpoints[`${import.meta.env.VITE_BRAND}`]),
      )

      let token = null
      if (organization.authentication) {
        token = await getToken(payload.username, payload.password)
      }

      // to-do: store this value securely
      setToLocalStorage('token', token)
      const { respbody, respcode, respmsg } = await apirequest(
        'validateUser',
        'POST',
        {
          PASSWD: payload.password,
          USERTYPE: payload.accountType,
          USERNAME: payload.username,
        },
      )

      if (respcode === 0) {
        accountData.value = { ...respbody }
        window.loading.value = false
        isAuthenticated.value = true
        setToLocalStorage('accountData', respbody)
        setToLocalStorage('authenticationTime', new Date().getTime())
        setToLocalStorage('lastUserActivityDate', Date.now())
        setToLocalStorage('hasAuthenticated', 'true')
        router.replace({ path: '/' })
      } else if (respcode === 2 && respbody) {
        questions.value = respbody.QNLIST
        router.push({ name: 'set-password' })
      } else if (respcode === 3) {
        try {
          setToLocalStorage('accountData', {
            USERTYPE: payload.accountType,
            USERNAME: payload.username,
          })
          router.push({ name: 'update-password' })
        } catch (e) {}
      } else {
        errorMessage.value = respmsg
        error.value = true

        throw respmsg || t('alerts.serverError')
      }
    } catch (error) {
      pushAlert({
        title: t('alerts.errorTitle'),
        message: error || t('alerts.genericError'),
        type: 'basic',
        notification_class: 'error',
        timeout: 5,
      })
    } finally {
      loading.value = false
    }
  }

  function logout() {
    const locale = getItemLocalStorage('locale') || 'en'
    const localeCode = getItemLocalStorage('localeCode') || 'en-US'
    localStorage.clear()
    setToLocalStorage('locale', locale)
    setToLocalStorage('localeCode', localeCode)
    isAuthenticated.value = false
    accountData.value = null
    window.location.href = '/auth/login'
  }

  const fetchAccountInfo = async (payload) => {
    try {
      window.loading.value = true
      const { respbody, respcode, respmsg } = await apirequest(
        'getAccountData',
        'POST',
        {
          CUST_ID: accountData.value?.CUSTID,
          USERNAME: accountData.value?.USERNAME,
        },
      )
      if (respcode === 0) {
        accountInfo.value = { ...respbody }
      } else {
        pushAlert({
          title: t('alerts.errorTitle'),
          message: respmsg || t('alerts.serverError'),
          type: 'basic',
          notification_class: 'error',
          timeout: 5,
        })
      }
      window.loading.value = false
    } catch (error) {
      window.loading.value = false

      pushAlert({
        title: t('alerts.errorTitle'),
        message: error || t('alerts.genericError'),
        type: 'basic',
        notification_class: 'error',
        timeout: 5,
      })
    }
  }

  return {
    accountTypes,
    isAuthenticated,
    accountTypes,
    accountData,
    accountInfo,
    section,
    questions,
    loading,
    error,
    errorMessage,
    lastUserActivityDate,
    hasResource,
    fetchAccountTypes,
    login,
    logout,
    updateSection,
    fetchAccountInfo,
    changePassword,
    setPassword,
    initResetPassword,
    validateSecurityAns,
    updatePassword,
    getToken,
    getToken,
  }
})

export default useAuthStore
